import React, { useState, useEffect } from 'react';

const ParentTab = ({ playerId }) => {
    const [parents, setParents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchParents = async () => {
            try {
                const response = await fetch(`https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1/parent/get-parents-by-player-id/${playerId}`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                if (!response.ok) throw new Error('Failed to fetch parents');
                const data = await response.json();
                setParents(data.data?.parents || []);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchParents();
    }, [playerId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading parent information...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                    <div className="text-red-400">
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">Error loading parent information</h3>
                        <p className="text-sm text-red-700 mt-1">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Parent Information</h2>
                {parents.length > 0 && (
                    <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                        {parents.length} parent{parents.length !== 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {parents.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <div className="text-gray-400 text-4xl mb-4">👨‍👩‍👧‍👦</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No parent information available</h3>
                    <p className="text-gray-600">This player hasn't added any parent contacts yet.</p>
                </div>
            ) : (
                parents.map((parent) => (
                    <div key={parent.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-200">
                        <div className="p-6">
                            <div className="flex items-start space-x-4">
                                {/* Profile Picture */}
                                <div className="flex-shrink-0">
                                    {parent.profilePicture ? (
                                        <img 
                                            src={parent.profilePicture} 
                                            alt={`${parent.firstName} ${parent.lastName}`}
                                            className="h-12 w-12 rounded-full object-cover border-2 border-gray-200"
                                        />
                                    ) : (
                                        <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                                            <span className="text-gray-600 font-medium text-lg">
                                                {parent.firstName?.[0]}{parent.lastName?.[0]}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Parent Details */}
                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-lg font-semibold text-gray-900">
                                            {parent.firstName} {parent.lastName}
                                        </h3>
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            {parent.relationship}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-500 mb-1">Username</h4>
                                            <p className="text-gray-900">@{parent.userName}</p>
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-500 mb-1">Email</h4>
                                            <p className="text-gray-900">{parent.email}</p>
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-500 mb-1">Phone</h4>
                                            <p className="text-gray-900">{parent.phone || 'Not provided'}</p>
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-500 mb-1">Account Type</h4>
                                            <p className="text-gray-900 capitalize">{parent.accountType}</p>
                                        </div>
                                    </div>

                                    {parent.roles && parent.roles.length > 0 && (
                                        <div className="mt-4 pt-4 border-t border-gray-100">
                                            <h4 className="text-sm font-medium text-gray-500 mb-2">Access Roles</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {parent.roles.map((role, index) => (
                                                    <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                        {role}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {parent.addedAt && (
                                        <div className="mt-3 text-xs text-gray-500">
                                            Added on {new Date(parent.addedAt).toLocaleDateString()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

export default ParentTab; 